<template>
  <!-- Rendered whenever the request succeeded, empty result included, so the
       section keeps its heading and its onward link instead of vanishing. A
       failed request still renders nothing: an error must never be presented as
       an empty result. -->
  <section
    v-if="previewActivities.length || isEmpty"
    id="jejak-aktivitas"
    class="gr-section gr-finished"
  >
    <div class="gr-container">
      <header class="gr-head gr-finished__head">
        <div class="gr-finished__head-copy">
          <p class="gr-eyebrow" aria-hidden="true">
            <span class="gr-eyebrow__index">04</span>
            <span class="gr-eyebrow__rule" />
          </p>
          <h2 class="gr-h2">Jejak Aktivitas</h2>
          <p class="gr-lede">Aktivitas yang sudah dijalankan.</p>
        </div>
        <NuxtLink
          :to="{ path: '/activities', query: { status: 'FINISHED' } }"
          class="gr-btn gr-btn--outline"
        >
          Lihat jejak aktivitas
          <v-icon icon="mdi-arrow-right" size="18" aria-hidden="true" />
        </NuxtLink>
      </header>

      <div v-if="previewActivities.length" class="gr-finished__grid">
        <ActivityCard
          v-for="activity in previewActivities"
          :key="activity.id"
          :to="activity.to"
          :content-img="activity.img"
          :content-title="activity.title"
          :content-date="activity.date"
          :content-city="activity.city"
          :heading-level="3"
          content-status="FINISHED"
        />
      </div>
      <div v-else class="gr-empty">
        <v-icon
          icon="mdi-calendar-blank-outline"
          size="20"
          aria-hidden="true"
        />
        <p class="gr-empty__text">
          Belum ada aktivitas yang selesai untuk ditampilkan.
        </p>
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
import ActivityCard from "@/features/home/views/components/ActivityCard.vue";

// A local fetch rather than useActivities(): that composable keeps its list in
// useState under fixed keys the activity list page also owns, so sharing it
// would let this three-card preview and the paginated list overwrite each
// other.
interface FinishedActivityResponseItem {
  uuid: string;
  slug: string;
  bannerUrl: string;
  title: string;
  location: string;
  registrationClosedDate: string;
  startDate: string;
  endDate: string;
}

interface FinishedActivitiesResponse {
  data: FinishedActivityResponseItem[];
}

const PREVIEW_SIZE = 3;

const { $customFetch } = useNuxtApp();

// sort=finished_recent is the API's `endDate DESC, id DESC` ordering, so the
// server returns the latest finished activities and nothing is sliced from a
// larger page here. Not caught: useAsyncData collects a rejection into its own
// error ref and Nuxt logs it, and `error` below keeps that failure out of the
// empty state.
const { data, error } = useAsyncData("site:finished-activities", () =>
  $customFetch<FinishedActivitiesResponse>("/public-activities", {
    method: "GET",
    params: {
      page: 1,
      size: PREVIEW_SIZE,
      status: "FINISHED",
      sort: "finished_recent",
    },
  }),
);

const dateFormatter = new Intl.DateTimeFormat("id-ID", {
  day: "numeric",
  month: "long",
  year: "numeric",
});

const formatDateRange = (startDate: string, endDate: string): string => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  return start.toDateString() === end.toDateString()
    ? dateFormatter.format(end)
    : `${dateFormatter.format(start)} - ${dateFormatter.format(end)}`;
};

// "KAB. ACEH SELATAN, ACEH" -> "ACEH SELATAN"
const extractCity = (location: string): string =>
  (location?.split(",")[0] ?? "").trim().replace(/^(KAB\.|KOTA)\s+/i, "");

// slice is a guard, not a filter: size=3 already bounds the request, but the
// card count must stay at most three whatever the API returns.
const previewActivities = computed(() =>
  (data.value?.data ?? []).slice(0, PREVIEW_SIZE).map((item) => ({
    id: item.uuid,
    to: `/activities/${item.slug}`,
    img: item.bannerUrl,
    title: item.title,
    date: formatDateRange(item.startDate, item.endDate),
    city: extractCity(item.location),
  })),
);

// Empty only when the request actually came back with nothing. A rejection
// leaves `data` null too, and reporting that as "no finished activities yet"
// would be a false statement about the data.
const isEmpty = computed(
  () =>
    !error.value && data.value != null && previewActivities.value.length === 0,
);
</script>

<style scoped>
.gr-finished__head {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1.25rem;
}

.gr-finished__head-copy {
  flex: 1 1 320px;
  min-width: 0;
}

.gr-finished__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 280px), 1fr));
  gap: 20px;
}
</style>
